import { apiFetch } from "./apiClient";

export type AlertaNivel = "critica" | "advertencia" | "info";
export type AlertaEstado = "activa" | "resuelta";

/**
 * Metadata de una alerta `stays_outbound_failed` en formato 2 (round
 * 2026-08-18). Las alertas anteriores solo traen `{action, recordId,
 * propertyId, error}` — de ahí que la UI tenga que tolerar campos ausentes y
 * que exista el botón «Reprocesar alertas antiguas», que las rellena.
 */
export interface AlertaStaysMeta {
  action?: "create" | "update" | "delete";
  /** Qué tipo de registro era. Sin esto el id es ambiguo: `reservas` y
   *  `bloqueos` numeran por separado y se solapan. */
  entidad?: "arriendo" | "bloqueo" | "mantencion" | "desconocido";
  recordId?: number | null;
  propertyId?: number | null;
  propiedadNombre?: string | null;
  /** Código visible de la plataforma (Airbnb/Booking), ej. "NN09J". */
  refPlataforma?: string | null;
  huesped?: string | null;
  /** `YYYY-MM-DD`. */
  desde?: string | null;
  hasta?: string | null;
  razon?: string | null;
  error?: string | null;
  /** Versión del formato del mensaje. Ausente o 1 = alerta vieja. */
  formato?: number;
  reformato?: { estado: string; en: string };
  original?: { titulo: string; mensaje: string | null };
  [k: string]: any;
}

export interface ReformatoResumen {
  revisadas: number;
  reformateadas: number;
  saltadas: number;
  fallidas: number;
  claveConflicto: number;
  porEstado: Record<string, number>;
  muestra: { id: number; estado: string; titulo: string }[];
  dryRun: boolean;
}

export interface Alerta {
  id: number;
  categoria: string;
  clave: string | null;
  nivel: AlertaNivel;
  titulo: string;
  mensaje: string | null;
  metadata: Record<string, any> | null;
  contador: number;
  estado: AlertaEstado;
  resueltaPor: number | null;
  resueltaEn: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Gestor de alertas administrativas. Ver backend/src/services/alerts.service.js
 * y docs/modulo-alertas.md. Todas las rutas requieren rol admin.
 */
export const alertsApi = {
  /** Lista alertas (por defecto las activas, más recientes primero). */
  listar: async (
    estado: AlertaEstado | "todas" = "activa",
    limit = 100,
  ): Promise<Alerta[]> => {
    const r = await apiFetch<{ success: boolean; data: Alerta[] }>(
      `/alertas?estado=${encodeURIComponent(estado)}&limit=${limit}`,
    );
    return r?.data ?? [];
  },

  /** Cantidad de alertas activas (badge de la campana). */
  contador: async (): Promise<number> => {
    const r = await apiFetch<{ success: boolean; data: { activas: number } }>(
      "/alertas/contador",
    );
    return r?.data?.activas ?? 0;
  },

  resolver: (id: number) =>
    apiFetch(`/alertas/${id}/resolver`, { method: "POST" }),

  reabrir: (id: number) =>
    apiFetch(`/alertas/${id}/reabrir`, { method: "POST" }),

  resolverTodas: () =>
    apiFetch("/alertas/resolver-todas", { method: "POST" }),

  eliminar: (id: number) =>
    apiFetch(`/alertas/${id}`, { method: "DELETE" }),

  /**
   * Reprocesa (re-encola) todos los webhooks de una propiedad por su listing
   * largo de Stays. Acción del flujo "registré la propiedad faltante →
   * recuperar sus reservas/bloqueos perdidos".
   */
  /**
   * Reescribe al formato legible las alertas viejas de sincronización con
   * Stays (las que decían «un registro local (id 1439)»). Idempotente: las que
   * ya están al día se saltan. Con `dryRun` calcula lo mismo sin escribir.
   */
  reformatearStays: async (opts?: {
    dryRun?: boolean;
    forzar?: boolean;
    incluirResueltas?: boolean;
  }): Promise<ReformatoResumen | null> => {
    const r = await apiFetch<{ success: boolean; data: ReformatoResumen }>(
      "/alertas/reformatear-stays",
      { method: "POST", body: JSON.stringify(opts ?? {}) },
    );
    return r?.data ?? null;
  },

  /**
   * Vuelve a empujar a Stays el registro de una alerta de sincronización.
   * El backend responde 409 (con el motivo en el toast) si la alerta no
   * identifica el registro sin ambigüedad, o si lo que falló fue un borrado.
   */
  reintentarStays: async (
    id: number,
  ): Promise<{
    sincronizado: boolean;
    idReserval: string | null;
    /**
     * Explicación que acompaña al resultado. Con `sincronizado: false`, el
     * reintento no pudo ni intentarlo (el sistema no sabe qué noches cubrir): no
     * hay nada que reintentar, hay que completar un dato. Con `sincronizado: true`,
     * la alerta se cerró SIN enviar nada (el bloqueo ya había terminado): no se
     * debe decir «enviado a Stays».
     */
    motivo?: string | null;
  } | null> => {
    const r = await apiFetch<{
      success: boolean;
      data: {
        sincronizado: boolean;
        idReserval: string | null;
        motivo?: string | null;
      };
    }>(`/alertas/${id}/reintentar-stays`, { method: "POST" });
    return r?.data ?? null;
  },

  reprocesarPropiedad: async (
    idPropiedadLargo: string,
  ): Promise<{ reset: number } | null> => {
    const r = await apiFetch<{ success: boolean; data: { reset: number } }>(
      "/alertas/reprocesar-propiedad",
      {
        method: "POST",
        body: JSON.stringify({ id_propiedad_largo: idPropiedadLargo }),
      },
    );
    return r?.data ?? null;
  },
};
